import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';
import { HreflangTags } from '@/components/seo/HreflangTags';
import { useGlobalContext } from '@/context/Global';
import { useInitUserTrackingWeb } from '@/hooks/useInitUserTrackingWeb';
import { buildCanonicalUrl } from '@/helpers/buildCanonicalUrl';
import { getLocaleFromPath } from '@/helpers/getLocaleFromPath';
import { windowIsReady } from '@/helpers/windowIsReady';
import { DEFAULT_LOCALE, ENV, PROJECT_TYPE, VALID_LOCALES } from '@/constants';
import seoImage from '@/public/seo-image.webp';
import styles from './LandingLayout.module.scss';

// Props Types
interface IProps {
	children: React.ReactNode;
	title?: string;
	description?: string;
	ogTitle?: string;
	ogDescription?: string;
}
const Header = dynamic(
	async () =>
		await import('@/components/modules/Landing/Header').then(mod => mod.Header),
);
const Footer = dynamic(
	async () =>
		await import('@/components/modules/Landing/Footer').then(mod => mod.Footer),
);
export const LandingLayout: React.FC<IProps> = ({
	children,
	title = 'Lift',
	description,
	ogTitle,
	ogDescription,
}) => {
	const doItOnce = React.useRef(true);
	const { isLoading } = useGlobalContext();
	const router = useRouter();
	const { asPath } = router;
	const currentPathForSeo = asPath?.split('?')[0] || '/';

	useInitUserTrackingWeb(doItOnce);

	const normalizedTitle = PROJECT_TYPE.PROD
		? title
		: `${title} [${ENV.PROJECT_TYPE}]`;

	const currentLocale = getLocaleFromPath(currentPathForSeo);
	const baseUrl = ENV.SEO_URL || 'https://liftapp.ai';
	const canonical = buildCanonicalUrl(
		baseUrl,
		currentPathForSeo,
		currentLocale,
		DEFAULT_LOCALE,
	);

	return (
		<div
			className={`${styles['layout']} ${
				isLoading ? styles['layout_opacity'] : ''
			}`}
		>
			<Head>
				<meta charSet='UTF-8' />
				<meta
					name='viewport'
					content='width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=5.0'
				/>
				<meta httpEquiv='X-UA-Compatible' content='ie=edge' />
				<meta name='mobile-web-app-capable' content='yes' />
				<meta name='theme-color' content='#ffffff' />
			</Head>
			<HreflangTags
				currentPath={currentPathForSeo}
				locales={VALID_LOCALES}
				defaultLocale={DEFAULT_LOCALE}
				baseUrl={baseUrl}
			/>
			<NextSeo
				title={normalizedTitle}
				description={description}
				canonical={canonical}
				openGraph={{
					url: windowIsReady ? window.location.href : '',
					title: ogTitle ?? normalizedTitle,
					description: ogDescription ?? description,
					locale: currentLocale,
					siteName: 'Lift',
					images: [
						{
							url: seoImage.src,
							width: 800,
							height: 600,
							alt: 'Lift App',
							type: 'image/webp',
						},
					],
				}}
			/>
			<Header />
			<main className='main'>{children}</main>
			<Footer />
		</div>
	);
};
